import tinycolor from '@kittl/tinycolor2';
import cloneDeep from 'lodash/cloneDeep';

import { ColorPaletteItemLinkedObject } from '../../types';

import { NONE_COLOR, RGB_BLACK, RGBA_TRANSPARENT } from './constants';
import {
  GradientObject,
  GradientColorStop,
  RGBAColorObject,
  GradientType,
  Color,
} from './types';
import { isEmptyColor } from './utils';

const DEFAULT_GRADIENT_COORDS = {
  [GradientType.Linear]: {
    p1: { x: 0, y: 0.5 },
    p2: { x: 1, y: 0.5 },
  },
  [GradientType.Radial]: {
    p1: { x: 0.5, y: 0.5 },
    p2: { x: 0.5, y: 0.5 },
  },
};

const DEFAULT_RADIAL_GRADIENT_RADII = { r1: 0, r2: 0.6 };

export const splitColorAndOpacity = (
  color: string,
  format: 'rgb' | 'hex' = 'rgb'
): { color: string; opacity: number } => {
  const solidColor = tinycolor(color);
  const opacity = solidColor.getAlpha();
  solidColor.setAlpha(1);
  return {
    color:
      format === 'hex' ? solidColor.toHexString() : solidColor.toRgbString(),
    opacity,
  };
};

export const getGradientAverage = (gradient: GradientObject): string => {
  const { colorStops } = gradient;
  const colors = colorStops!.map((cs: GradientColorStop) => {
    let { color } = cs;

    if (isEmptyColor(color)) {
      color = RGBA_TRANSPARENT;
    }

    return tinycolor(color).toRgb();
  });

  const avg = colors.reduce(
    (sumColor: RGBAColorObject, color: RGBAColorObject) => {
      sumColor.r += color.r;
      sumColor.g += color.g;
      sumColor.b += color.b;
      sumColor.a += color.a;
      return sumColor;
    },
    { r: 0, g: 0, b: 0, a: 0 }
  );

  avg.r /= colorStops!.length;
  avg.g /= colorStops!.length;
  avg.b /= colorStops!.length;
  avg.a /= colorStops!.length;

  return tinycolor(avg).toRgbString();
};

export const gradientObjectFromColor = (
  color: string | null,
  gradientType: GradientType
): GradientObject => {
  const {
    p1: { x: x1, y: y1 },
    p2: { x: x2, y: y2 },
  } = DEFAULT_GRADIENT_COORDS[gradientType];

  const coords =
    gradientType === GradientType.Radial
      ? { x1, y1, x2, y2, ...DEFAULT_RADIAL_GRADIENT_RADII }
      : { x1, y1, x2, y2 };

  return {
    type: gradientType,
    colorStops: [
      { color: color ?? NONE_COLOR, offset: 0 },
      { color: RGB_BLACK, offset: 1 },
    ],
    coords,
    angle: -90,
    gradientTransform: [1, 0, 0, 1, 0, 0],
  };
};

export const changeGradientType = (
  gradient: GradientObject,
  gradientType: GradientType
): GradientObject => {
  const newGradient = cloneDeep(gradient);

  if (newGradient.coords) {
    const { p1, p2 } = DEFAULT_GRADIENT_COORDS[gradientType];
    const coords = {
      x1: p1.x,
      y1: p1.y,
      x2: p2.x,
      y2: p2.y,
    };
    newGradient.type = gradientType;
    newGradient.gradientTransform = [1, 0, 0, 1, 0, 0];
    newGradient.coords =
      gradientType === GradientType.Radial
        ? {
            ...DEFAULT_RADIAL_GRADIENT_RADII,
            ...coords,
          }
        : { ...coords };
  }

  return newGradient;
};

export const isGradientObject = (
  obj: Color | ColorPaletteItemLinkedObject['color'] | null | undefined
): obj is GradientObject => {
  return (
    !!obj && typeof obj === 'object' && 'angle' in obj && 'colorStops' in obj
  );
};

/**
 * Get a non-gradient color from a color object.
 */
export const getSolidColor = (
  color: Color,
  defaultColor?: string | null
): string | null => {
  if (isGradientObject(color) || color === undefined) {
    return defaultColor ?? null;
  }

  return color ?? null;
};
